import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { jwtDecode } from "jwt-decode";
import { baseUrl } from "../services/api";
import {
  AUTH_PROVIDER_KEY,
  AZURE_SSO_SESSION_KEY,
  MICROSOFT_AUTH_PROVIDER,
  MICROSOFT_SESSION_USER_KEY,
  MS_LOGIN_EMAIL_KEY,
  MS_LOGIN_PENDING_KEY,
} from "./authSessionKeys";
import {
  getAzureApiScopes,
  getAzureRedirectUri,
  getTokenRequest,
  isMicrosoftAuthConfigured,
} from "./msalConfig";
import { msalInstance } from "./msalInstance";

const region = process.env.REACT_APP_REGION || "America/Los_Angeles";

export function isMicrosoftSession() {
  return localStorage.getItem(AUTH_PROVIDER_KEY) === MICROSOFT_AUTH_PROVIDER;
}

export function clearMicrosoftLocalSession() {
  localStorage.removeItem(AUTH_PROVIDER_KEY);
  localStorage.removeItem(MICROSOFT_SESSION_USER_KEY);
  localStorage.removeItem(AZURE_SSO_SESSION_KEY);
  sessionStorage.removeItem(MS_LOGIN_PENDING_KEY);
  sessionStorage.removeItem(MS_LOGIN_EMAIL_KEY);
}

/**
 * Maps POST /api/azure-login user payload into the app's userInfo shape.
 * Merged with Django JWT claims so selectors match password-login sessions.
 */
function buildSessionUserFromAzureLogin(user = {}, accessToken = "") {
  let claims = {};
  try {
    claims = accessToken ? jwtDecode(accessToken) || {} : {};
  } catch {
    claims = {};
  }

  const apiUser = {
    email: user.email || null,
    role: user.role || null,
    user_id: user.id ?? user.user_id ?? null,
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    role_label: user.role_label,
    is_registered:
      user.is_registered !== undefined ? user.is_registered : Boolean(user.role),
    is_verified: user.is_verified,
    is_pwd_changed:
      user.is_pwd_changed !== undefined ? user.is_pwd_changed : true,
    member_active: user.member_active,
    userinfo: user.userinfo,
  };

  // Django JWT is the same source of truth as password login (updateInfo / getUserInfo)
  return {
    ...apiUser,
    ...claims,
    user_id: claims.user_id ?? apiUser.user_id,
    email: claims.email ?? apiUser.email,
    role: claims.role ?? apiUser.role,
    first_name: claims.first_name ?? apiUser.first_name,
    last_name: claims.last_name ?? apiUser.last_name,
    is_registered:
      claims.is_registered !== undefined
        ? claims.is_registered
        : apiUser.is_registered,
    is_pwd_changed:
      claims.is_pwd_changed !== undefined
        ? claims.is_pwd_changed
        : apiUser.is_pwd_changed,
    userinfo: claims.userinfo ?? apiUser.userinfo,
  };
}

function persistSessionUser(user) {
  localStorage.setItem(MICROSOFT_SESSION_USER_KEY, JSON.stringify(user));
}

/**
 * Exchanges a Microsoft token for Django JWTs.
 * POST /api/azure-login
 * Authorization: Bearer <Microsoft idToken preferred, or access token>
 */
async function exchangeMicrosoftTokenWithDjango(microsoftToken) {
  const response = await axios.post(
    `${baseUrl}azure-login`,
    {},
    {
      headers: {
        Authorization: `Bearer ${microsoftToken}`,
        "Content-Type": "application/json",
        Region: region,
      },
      validateStatus: () => true,
    }
  );

  if (response.status === 401 || response.status === 403) {
    const message =
      response.data?.message ||
      response.data?.detail ||
      response.data?.error ||
      "Microsoft sign-in was rejected.";
    const error = new Error(
      typeof message === "string" ? message : "Microsoft sign-in was rejected."
    );
    error.status = response.status;
    throw error;
  }

  if (response.status >= 400) {
    const message =
      response.data?.message ||
      response.data?.detail ||
      response.data?.error ||
      "Unable to complete Microsoft sign-in.";
    throw new Error(
      typeof message === "string" ? message : "Unable to complete Microsoft sign-in."
    );
  }

  return response.data && typeof response.data === "object" ? response.data : {};
}

/**
 * Acquires Microsoft tokens via MSAL (Authorization Code + PKCE).
 * Prefers idToken for azure-login; falls back to accessToken.
 */
export async function acquireMicrosoftTokensForAzureLogin(
  instance = msalInstance
) {
  if (!isMicrosoftAuthConfigured()) {
    throw new Error(
      "Microsoft sign-in is not configured. Set REACT_APP_AZURE_CLIENT_ID, REACT_APP_AZURE_TENANT_ID or REACT_APP_AZURE_AUTHORITY, REACT_APP_AZURE_REDIRECT_URI, and REACT_APP_AZURE_API_SCOPE."
    );
  }

  if (!getAzureApiScopes().length) {
    throw new Error(
      "REACT_APP_AZURE_API_SCOPE is required to acquire a Microsoft token."
    );
  }

  const account =
    instance.getActiveAccount() || instance.getAllAccounts() [0] || null;

  if (!account) {
    throw new Error("No Microsoft account is signed in.");
  }

  instance.setActiveAccount(account);

  try {
    const result = await instance.acquireTokenSilent(getTokenRequest(account));
    // Django azure-login: idToken preferred; access token (e.g. Graph) also accepted
    const microsoftToken = result?.idToken || result?.accessToken;

    if (!microsoftToken) {
      throw new Error(
        "Microsoft did not return a token. Check REACT_APP_AZURE_API_SCOPE."
      );
    }

    return {
      microsoftToken,
      idToken: result?.idToken || "",
      accessToken: result?.accessToken || "",
      account,
    };
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      throw new Error("Microsoft sign-in expired. Please sign in again.");
    }
    throw error;
  }
}

/** @deprecated Prefer acquireMicrosoftTokensForAzureLogin */
export async function acquireMicrosoftAccessToken(instance = msalInstance) {
  const tokens = await acquireMicrosoftTokensForAzureLogin(instance);
  return tokens.accessToken || tokens.microsoftToken;
}

/**
 * Completes Microsoft SSO:
 * 1) Acquire Microsoft idToken/accessToken via MSAL
 * 2) POST /api/azure-login with Authorization: Bearer <token>
 * 3) Store Django access/refresh JWTs and return mapped user
 */
export async function completeMicrosoftLogin(instance = msalInstance) {
  const { microsoftToken } = await acquireMicrosoftTokensForAzureLogin(instance);
  const payload = await exchangeMicrosoftTokenWithDjango(microsoftToken);

  const appAccessToken =
    typeof payload?.access === "string" ? payload.access : "";
  const appRefreshToken =
    typeof payload?.refresh === "string" ? payload.refresh : "";

  if (!appAccessToken) {
    throw new Error(
      "Azure login did not return an application access token."
    );
  }

  localStorage.setItem("access_token", appAccessToken);
  if (appRefreshToken) {
    localStorage.setItem("refresh_token", appRefreshToken);
  } else {
    localStorage.removeItem("refresh_token");
  }

  // Same session shape as password login: Django JWTs in localStorage.
  // Mark SSO so PrivateRoute does not force /change-password (no current password).
  clearMicrosoftLocalSession();
  localStorage.setItem(AZURE_SSO_SESSION_KEY, "1");
  const user = buildSessionUserFromAzureLogin(payload.user || {}, appAccessToken);
  // Frontend routing: SSO users are not sent through password-change gate
  user.is_pwd_changed = true;
  persistSessionUser(user);

  // App no longer needs Microsoft tokens in browser storage after azure-login.
  await clearMsalCache();

  return {
    user,
    permissions: payload.permissions || {},
    codenames: payload.codenames || [],
    usedAppTokens: true,
  };
}

export async function refreshMicrosoftAccessToken() {
  const tokens = await acquireMicrosoftTokensForAzureLogin();
  const payload = await exchangeMicrosoftTokenWithDjango(tokens.microsoftToken);
  if (typeof payload?.access === "string" && payload.access) {
    localStorage.setItem("access_token", payload.access);
    if (typeof payload?.refresh === "string" && payload.refresh) {
      localStorage.setItem("refresh_token", payload.refresh);
    }
    localStorage.setItem(AZURE_SSO_SESSION_KEY, "1");
    return payload.access;
  }
  throw new Error("Unable to refresh Microsoft application session.");
}

async function clearMsalCache() {
  try {
    if (typeof msalInstance.clearCache === "function") {
      await msalInstance.clearCache();
    }
  } catch (error) {
    console.error("Failed to clear MSAL cache", error);
  }

  // Remove any leftover MSAL keys from both storages (including older localStorage cache)
  [localStorage, sessionStorage].forEach((storage) => {
    Object.keys(storage).forEach((key) => {
      if (key.startsWith("msal.") || key.startsWith("msal.3")) {
        storage.removeItem(key);
      }
    });
  });
}

/**
 * Ends the Entra session. Returns true when the browser is leaving for Microsoft logout.
 */
export async function logoutMicrosoft() {
  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;

  if (!account) {
    await clearMsalCache();
    return false;
  }

  await msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri: getAzureRedirectUri(),
  });
  return true;
}

export function getMicrosoftAuthErrorMessage(error) {
  const code = error?.errorCode || error?.code || "";
  if (
    code === "user_cancelled" ||
    code === "access_denied" ||
    error?.message?.toLowerCase?.().includes("user cancelled")
  ) {
    return "Microsoft sign-in was cancelled.";
  }
  if (code === "interaction_in_progress") {
    return "Microsoft sign-in is already in progress.";
  }
  return (
    error?.message ||
    "Microsoft sign-in failed. Please try again."
  );
}

export { MS_LOGIN_PENDING_KEY };
